import { registerLocaleData } from '@angular/common';
import { LOCALE_ID, Provider } from '@angular/core';
import localeDeAt from '@angular/common/locales/de-AT';
import localeEnGb from '@angular/common/locales/en-GB';

/**
 * Where {@link LanguageService} persists the chosen language. Duplicated rather than imported to
 * keep this file free of DI: `LOCALE_ID` is resolved while the injector is still being built, so the
 * factory below cannot depend on a service that itself depends on the injector.
 */
const STORAGE_KEY = 'jjames.lang';

/**
 * Language code → the locale its formats come from.
 *
 * `de-AT` rather than `de`: Austrian date order and the €-after-the-number convention.
 * `en-GB` rather than `en-US`: this is a European product where times are read off a screen next to
 * a barrier, and "12:31" is unambiguous where "12:31 PM" invites the reader to work out whether
 * midday counts as AM. Keeping both languages on 24-hour time also means a German and an English
 * user comparing the same stay see the same digits.
 */
const LOCALE_BY_LANG: Record<string, string> = {
  de: 'de-AT',
  en: 'en-GB',
};

const DEFAULT_LOCALE = 'de-AT';

let registered = false;

function registerOnce(): void {
  if (registered) return;
  registerLocaleData(localeDeAt, 'de-AT');
  registerLocaleData(localeEnGb, 'en-GB');
  registered = true;
}

/**
 * The locale to format dates, times and money in, from the same stored choice
 * {@link LanguageService} reads.
 *
 * Mirrors that service's resolution order (stored → browser → default) deliberately: a user whose
 * interface is German but whose dates render American is a worse result than either language alone,
 * and that is exactly what happens when translations are wired up and `LOCALE_ID` is left at its
 * `en-US` default — which is what every JJames app did until this existed.
 */
export function resolveAppLocale(): string {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* localStorage unavailable (private mode) — fall through to the browser */
  }

  if (stored && LOCALE_BY_LANG[stored]) return LOCALE_BY_LANG[stored];

  const browser = (navigator?.language ?? '').split('-')[0].toLowerCase();
  return LOCALE_BY_LANG[browser] ?? DEFAULT_LOCALE;
}

/**
 * Registers the locale data these apps ship and binds `LOCALE_ID` to the active language.
 *
 * Add to an app's `providers` alongside `provideTranslateService`. Note Angular reads `LOCALE_ID`
 * once, when the injector is created: switching language at runtime re-translates the text but does
 * not reformat dates already on screen until the next full load. That is an Angular constraint, not
 * an oversight here — the alternative is a locale-aware wrapper around every pipe.
 */
export function provideAppLocale(): Provider {
  registerOnce();
  return { provide: LOCALE_ID, useFactory: resolveAppLocale };
}
